<template>
  <div
    data-a4-page
    class="relative block h-[1754px] w-[1240px] bg-white"
    style="-webkit-print-color-adjust: exact"
  >
    <!-- Header: Photo + Name + Contact -->
    <div class="absolute left-[75px] top-[73px] flex items-center">
      <!-- Photo -->
      <div
        v-if="hasPhoto"
        class="h-[256px] w-[254px] shrink-0 overflow-hidden bg-gray-100"
      >
        <img
          v-if="profileImageUrl"
          :src="profileImageUrl"
          class="h-full w-full object-cover"
        />
        <image-preview
          v-else
          :uuid="photoUuid"
          :width="PORTFOLIO_PREVIEW_IMAGE_WIDTH"
          class="h-full w-full object-cover"
        />
      </div>

      <!-- Cyan left border + Name + Contact -->
      <div
        class="flex h-[256px] flex-col justify-center gap-[16px] border-l-[6px] border-[#b8fdff] pl-[40px]"
        :class="hasPhoto ? 'ml-[50px]' : ''"
      >
        <p class="text-[25px] leading-[38px] text-[#1d1d1f]">
          {{ account.firstName }} {{ account.lastName }}
        </p>
        <div class="flex flex-col gap-[10px]">
          <div class="flex items-center gap-[16px]">
            <svg-icon
              name="phone"
              class="h-[25px] w-[25px] shrink-0 text-[#1d1d1f]"
            />
            <span class="text-[22px] font-bold leading-[32px] text-[#1d1d1f]"
              >Telefon</span
            >
            <span class="text-[22px] leading-[32px] text-[#1d1d1f]">{{
              account.phone
            }}</span>
          </div>
          <div class="flex items-center gap-[16px]">
            <svg-icon
              name="envelope"
              class="h-[25px] w-[25px] shrink-0 text-[#1d1d1f]"
            />
            <span class="text-[22px] font-bold leading-[32px] text-[#1d1d1f]"
              >E-mail</span
            >
            <span class="text-[22px] leading-[32px] text-[#1d1d1f]">{{
              account.email
            }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Main heading + Anlagen -->
    <div class="absolute left-[84px] top-[470px] flex flex-col gap-[75px]">
      <p
        class="w-[1050px] pr-[50px] text-[47px] font-bold leading-[56px] tracking-[-0.95px] text-[#1d1d1f]"
      >
        Bewerbung um einen Praxisplatz im Rahmen eines Dualen Studiums
      </p>

      <div class="flex flex-col gap-[10px]">
        <p
          class="text-[37px] font-bold leading-[45px] tracking-[-0.76px] text-[#1d1d1f]"
        >
          Anlagen:
        </p>
        <ul
          class="list-disc pl-[33px] text-[22px] leading-[32px] text-[#1d1d1f]"
        >
          <li>Lebenslauf</li>
          <li>Persönliche Vorstellung</li>
        </ul>
      </div>
    </div>

    <!-- Footer wave separator -->
    <div class="absolute left-0 top-[1380px] w-full overflow-hidden">
      <svg
        viewBox="0 0 794 57"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="w-full"
        preserveAspectRatio="none"
      >
        <path d="M0 55L794 0V57H0Z" fill="#d2fafb" />
      </svg>
    </div>

    <!-- Footer cyan background -->
    <div
      class="absolute bottom-0 left-0 flex h-[287px] w-full bg-[#d2fafb] px-[75px] pt-[75px]"
    >
      <!-- IU Logo -->
      <div class="shrink-0 pt-[4px]">
        <img
          src="@/assets/images/iu-logo-horizontal.svg"
          alt="IU Logo"
          class="h-[65px]"
        />
      </div>

      <!-- Footer text -->
      <div class="ml-[50px] flex flex-col gap-[4px]">
        <p class="text-[25px] leading-[38px] text-[#364153]">
          Bei Fragen zum Dualen Studium wenden Sie sich bitte an die
          Studienberatung der IU Internationalen Hochschule:
        </p>
        <div class="flex items-center gap-[6px]">
          <div class="flex items-center gap-[12px]">
            <svg-icon
              name="envelope"
              class="h-[25px] w-[25px] shrink-0 text-[#364153]"
            />
            <span
              class="text-[25px] font-bold leading-[38px] text-[#364153] underline"
              >unternehmen@iu.org</span
            >
          </div>
          <div class="ml-[12px] flex items-center gap-[12px]">
            <svg-icon
              name="phone"
              class="h-[25px] w-[25px] shrink-0 text-[#364153]"
            />
            <span
              class="text-[25px] font-bold leading-[38px] text-[#364153] underline"
              >+49 36164604050</span
            >
          </div>
        </div>
      </div>
    </div>

    <!-- Page number -->
    <p
      class="absolute bottom-[62px] right-[75px] text-[22px] leading-[32px] text-[#1d1d1f]"
    >
      1 | {{ totalDocPages }} {{ account.firstName }} {{ account.lastName }} |
      Deckblatt
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import ImagePreview from '@/components/ImagePreview.vue'
import SvgIcon from '@/components/SvgIcon.vue'
import accountStore from '@/stores/account.store'
import documentsStore from '@/stores/documents.store'
import { PORTFOLIO_PREVIEW_IMAGE_WIDTH } from './preview-image.constants'
import { DocumentType } from '@/enums'

const props = withDefaults(
  defineProps<{
    totalDocPages?: number
    profileImageUrl?: string
  }>(),
  { totalDocPages: 3, profileImageUrl: undefined },
)

const account = computed(() => accountStore.state.account)

const photoUuid = computed(() => {
  const pics = documentsStore.getDocumentsByCategory(DocumentType.Picture)
  return pics.length > 0 ? pics[0].uuid : null
})

const hasPhoto = computed(() => !!props.profileImageUrl || !!photoUuid.value)
</script>
